import { useCallback } from "react";
import { usePosterProject } from "PosterEditor/model/poster-project-provider";
import { StaticLabel, StravaActivityMetadata } from "PosterEditor/model/poster-project";
import {
  formatStravaDistance,
  formatStravaElevation,
  formatStravaDate,
} from "PosterEditor/utils/strava-formatters";

/**
 * Minimum elevation gain in meters below which elevation is not displayed.
 * Activities with less than 10m elevation gain (e.g., flat runs) don't need
 * elevation shown as it's not a meaningful metric for those activities.
 */
const MINIMUM_ELEVATION_METERS = 10;

/**
 * Hook for populating static labels from Strava activity metadata.
 *
 * Labels are populated in priority order:
 * 1. Activity name (goes to largest font label)
 * 2. Distance
 * 3. Elevation (if >= 10m)
 * 4. Date
 *
 * Labels are sorted by font size (largest first) to ensure the most prominent
 * label gets the activity name. For labels of the same size, they are sorted
 * by position (top-to-bottom, left-to-right) for consistent, predictable ordering.
 *
 * All visible labels are overwritten when a Strava route is selected,
 * regardless of whether they've been previously edited.
 */
export const useStravaLabelPopulation = () => {
  const { project, setStaticLabel } = usePosterProject();

  /**
   * Returns labels that can be populated (visible labels only).
   */
  const getPopulatableLabels = useCallback((): StaticLabel[] => {
    return project.staticLabels.filter((label) => !label.style.isHidden);
  }, [project.staticLabels]);

  /**
   * Returns true if there are labels that can be populated with Strava data.
   */
  const hasPopulatableLabels = useCallback((): boolean => {
    return getPopulatableLabels().length > 0;
  }, [getPopulatableLabels]);

  /**
   * Sorts labels for population order:
   * 1. By font size descending (largest first)
   * 2. By position: top-to-bottom, then left-to-right (for same font size)
   */
  const sortLabelsForPopulation = useCallback((labels: StaticLabel[]): StaticLabel[] => {
    return [...labels].sort((a, b) => {
      // Primary sort: font size descending
      const fontSizeDiff = b.style.fontSize - a.style.fontSize;
      if (fontSizeDiff !== 0) {
        return fontSizeDiff;
      }

      // Secondary sort: position (top-to-bottom, then left-to-right)
      // Lower y value = higher on screen = comes first
      const yDiff = a.position.y - b.position.y;
      if (Math.abs(yDiff) > 0.001) {
        return yDiff;
      }

      // Tertiary sort: left-to-right
      return a.position.x - b.position.x;
    });
  }, []);

  /**
   * Builds an array of data items to populate into labels, in priority order.
   * Only includes elevation if it meets the minimum threshold.
   */
  const buildDataItems = useCallback((metadata: StravaActivityMetadata): string[] => {
    const items: string[] = [];

    // Priority 1: Activity name
    items.push(metadata.activityName);

    // Priority 2: Distance
    items.push(formatStravaDistance(metadata.distance));

    // Priority 3: Elevation (only if significant)
    if (metadata.elevationGain >= MINIMUM_ELEVATION_METERS) {
      items.push(formatStravaElevation(metadata.elevationGain));
    }

    // Priority 4: Date
    items.push(formatStravaDate(metadata.startDate));

    return items;
  }, []);

  /**
   * Populates static labels with Strava activity data.
   *
   * Labels are matched to data in priority order:
   * - Largest font labels get activity name
   * - Smaller labels get distance, elevation (if significant), and date
   *
   * Only labels that haven't been edited and aren't hidden will be populated.
   */
  const populateLabelsFromStrava = useCallback((metadata: StravaActivityMetadata): void => {
    const populatableLabels = getPopulatableLabels();

    if (populatableLabels.length === 0) {
      return;
    }

    const sortedLabels = sortLabelsForPopulation(populatableLabels);
    const dataItems = buildDataItems(metadata);

    // Assign data to labels by position in sorted array
    sortedLabels.forEach((label, index) => {
      if (index < dataItems.length) {
        const updatedLabel: StaticLabel = {
          ...label,
          text: dataItems[index],
          // Note: We don't set isEdited here because this is auto-population,
          // not user editing. Labels can still be re-populated on the next import.
        };
        setStaticLabel(updatedLabel);
      }
    });
  }, [getPopulatableLabels, sortLabelsForPopulation, buildDataItems, setStaticLabel]);

  return {
    populateLabelsFromStrava,
    hasPopulatableLabels,
  };
};
